import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  doc,
  onSnapshot,
  getDoc,
  collection,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { supabase } from "../../supabase";
import driver from "../assets/images/driver.png";
import { HiDotsVertical } from "react-icons/hi";
import { FaPhone } from "react-icons/fa6";
import Cropper from "react-easy-crop";
import { toast } from "react-toastify";
import { formatINR } from "../../utils/currency";
import { GST } from "../../API_KEYS";
import { RiH6 } from "react-icons/ri";

function BookingDetails() {
  const { id } = useParams();

  const [booking, setBooking] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [vehicle, setVehicle] = useState(null);

  const [isSavingDriver, setIsSavingDriver] = useState(false);

  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [allVehicles, setAllVehicles] = useState([]);
  const [allBookings, setAllBookings] = useState([]);

  const [selectedVehicleId, setSelectedVehicleId] = useState(null);

  const [showVehDetailsModal, setShowVehDetailsModal] = useState(false);

  const [showDriverActions, setShowDriverActions] = useState(false);

  const [vehDetails, setVehDetails] = useState({
    veh_number: "",
    veh_color: "",
  });

  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);

  useEffect(() => {
    if (booking) {
      setVehDetails({
        veh_number: booking.veh_number || "",
        veh_color: booking.veh_color || "",
      });
    }
  }, [booking]);

  const [drivers, setDrivers] = useState(null);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [allDrivers, setAllDrivers] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState(null);

  const [showAddDriverModal, setShowAddDriverModal] = useState(false);

  const [newDriver, setNewDriver] = useState({
    name: "",
    phone: "",
    profile_img: "",
  });

  const fetchDrivers = async () => {
    const { data, error } = await supabase.from("Drivers").select("*");

    if (!error) setAllDrivers(data);
  };

  useEffect(() => {
    if (booking?.driver_id) {
      setSelectedDriverId(booking.driver_id);
    }
  }, [booking]);

  useEffect(() => {
    if (booking?.vehicle_id) {
      setSelectedVehicleId(booking.vehicle_id);
    }
  }, [booking]);

  /* -------------------------------------------------- */
  /*  FETCH BOOKING + CUSTOMER                         */
  /* -------------------------------------------------- */

  useEffect(() => {
    if (!id) return;

    const unsubscribe = onSnapshot(
      doc(db, "LiveBookings", id),
      async (snap) => {
        if (!snap.exists()) return;

        const bookingData = snap.data();
        setBooking(bookingData);

        // Fetch customer
        if (bookingData.customer_id) {
          const userSnap = await getDoc(
            doc(db, "Users", bookingData.customer_id),
          );
          if (userSnap.exists()) {
            setCustomer(userSnap.data());
          }
        }

        // Fetch vehicle if assigned
        if (bookingData.vehicle_id) {
          const { data } = await supabase
            .from("Vehicles")
            .select("*")
            .eq("id", bookingData.vehicle_id)
            .single();

          if (data) setVehicle(data);
        } else {
          setVehicle(null);
        }

        // Fetch driver if assigned
        if (bookingData.driver_id) {
          const { data } = await supabase
            .from("Drivers")
            .select("*")
            .eq("id", bookingData.driver_id)
            .single();

          if (data) setDrivers(data);
        } else {
          setDrivers(null);
        }
      },
    );

    return () => unsubscribe();
  }, [id]);

  /* -------------------------------------------------- */
  /*  FETCH ALL VEHICLES                               */
  /* -------------------------------------------------- */

  const fetchVehicles = async () => {
    const { data, error } = await supabase
      .from("Vehicles")
      .select("*")
      .order("type", { ascending: false });
    if (!error) setAllVehicles(data);
  };

  /* -------------------------------------------------- */
  /*  FETCH ALL BOOKINGS (FOR OVERLAP CHECK)           */
  /* -------------------------------------------------- */

  useEffect(() => {
    fetchVehicles();
    fetchDrivers();

    const unsubscribe = onSnapshot(
      collection(db, "LiveBookings"),
      (snapshot) => {
        const list = [];
        snapshot.forEach((doc) => {
          list.push({ id: doc.id, ...doc.data() });
        });
        setAllBookings(list);
      },
    );

    return () => unsubscribe();
  }, []);

  /* -------------------------------------------------- */
  /*  ASSIGN VEHICLE                                   */
  /* -------------------------------------------------- */

  const getUpdatedPrice = (selectedVehicle, booking) => {
    if (!selectedVehicle || !booking) return 0;

    const basePrice = Number(booking.total_price);

    if (selectedVehicle.type === booking.vehicle_type) {
      return basePrice;
    }

    if (
      selectedVehicle.type === "economy" &&
      booking.vehicle_type === "premium"
    ) {
      return basePrice - 1000;
    }

    if (
      selectedVehicle.type === "premium" &&
      booking.vehicle_type === "economy"
    ) {
      return basePrice + 1000;
    }

    return basePrice;
  };

  const confirmVehicleSelection = async () => {
    if (!selectedVehicleId) return;

    const selectedVehicle = allVehicles.find((v) => v.id === selectedVehicleId);
    if (!selectedVehicle) return;

    // 🔥 VALIDATION (only for non-Reserve)
    if (booking.booking_type !== "Reserve") {
      const price = booking.total_price;

      // Check if string OR invalid number
      if (typeof price === "string" || isNaN(Number(price))) {
        toast.error("Enter a valid ride price");
        return;
      }

      if (Number(price) <= 0) {
        toast.error("Price must be greater than 0");
        return;
      }
    }

    // Update Firestore
    await updateDoc(doc(db, "LiveBookings", id), {
      vehicle_id: selectedVehicleId,
      vehicle_type: selectedVehicle.type, // <-- update vehicle_type
      veh_number: "",
      veh_color: "",

      // 🔥 NEW FIELD RESET
      confirm_price: 0,

      daily_rate:
        booking.booking_type == "Reserve"
          ? parseFloat(selectedVehicle.daily_price)
          : booking.daily_rate,
      km_price:
        booking.booking_type == "Reserve"
          ? parseInt(selectedVehicle.km_price)
          : booking.km_price,
      total_price:
        booking.booking_type === "Reserve"
          ? parseFloat(selectedVehicle.daily_price * booking.NoOfDays)
          : Number(booking.total_price), // 🔥 use updated input value
    });

    setShowVehicleModal(false);
    setSelectedVehicleId(null);
  };

  const [selectedImage, setSelectedImage] = useState(null);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setImageSrc(reader.result);
      setShowCropModal(true);
    };
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const getCroppedWebpImage = async () => {
    const image = new Image();
    image.src = imageSrc;

    await new Promise((resolve) => {
      image.onload = resolve;
    });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const size = 800; // final square size

    canvas.width = size;
    canvas.height = size;

    ctx.drawImage(
      image,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      size,
      size,
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/webp", 0.8);
    });
  };

  const isDriverBooked = (driverId) => {
    if (!booking?.pickupTime || !booking?.NoOfDays) return false;

    const currentStart = new Date(booking.pickupTime.seconds * 1000);

    const currentEnd = new Date(currentStart);
    currentEnd.setDate(currentEnd.getDate() + booking.NoOfDays);

    return allBookings.some((b) => {
      if (b.id === id) return false;
      if (b.driver_id !== driverId) return false;
      if (!b.pickupTime || !b.NoOfDays) return false;

      const bookedStart = new Date(b.pickupTime.seconds * 1000);

      const bookedEnd = new Date(bookedStart);
      bookedEnd.setDate(bookedEnd.getDate() + b.NoOfDays);

      return currentStart < bookedEnd && currentEnd > bookedStart;
    });
  };

  const confirmDriverSelection = async () => {
    if (!selectedDriverId) return;

    await updateDoc(doc(db, "LiveBookings", id), {
      driver_id: selectedDriverId,
      status: 1,
    });

    // 🔥 fetch and set driver immediately
    const { data } = await supabase
      .from("Drivers")
      .select("*")
      .eq("id", selectedDriverId)
      .single();

    if (data) setDrivers(data);

    setShowDriverModal(false);
    setSelectedDriverId(null);
  };

  const updateVehicleDetails = async () => {
    if (!vehDetails.veh_number || !vehDetails.veh_color) {
      toast.error("Please fill in all vehicle details");
      return;
    }
    try {
      await updateDoc(doc(db, "LiveBookings", id), {
        veh_number: vehDetails.veh_number.toUpperCase(),
        veh_color: vehDetails.veh_color,
      });

      setShowVehDetailsModal(false);
    } catch (error) {
      console.error("Error updating vehicle details:", error);
    }
  };

  /* -------------------------------------------------- */

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp.seconds * 1000).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const calculateRideDays = (start, end) => {
    if (!start || !end) return 0;

    // Convert Firestore Timestamp to JS Date
    const startDate = start?.seconds
      ? new Date(start.seconds * 1000)
      : new Date(start);

    const endDate = end?.seconds ? new Date(end.seconds * 1000) : new Date(end);

    const diffInMs = endDate - startDate;

    if (diffInMs <= 0) return 0;

    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));

    return diffInDays;
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/driver-booking/${id}`;
    const shareText = "Driver Dashboard";

    try {
      if (navigator.share) {
        // Mobile / supported browsers
        await navigator.share({
          title: "Driver Booking",
          text: shareText,
          url: shareUrl,
        });
      } else {
        // Fallback for desktop
        await navigator.clipboard.writeText(shareUrl);
        alert("Link copied to clipboard!");
      }
    } catch (error) {
      console.error("Sharing failed:", error);
    }
  };

  const getVehiclePrice = (v, booking) => {
    if (!booking || !booking.total_price) return "0.00";

    const basePrice = Number(booking.total_price);

    // Define hierarchy
    const priceMap = {
      economy: 0,
      standard: 1000,
      premium: 2000,
    };

    const bookingOffset = priceMap[booking.vehicle_type] || 0;
    const vehicleOffset = priceMap[v.type] || 0;

    // Adjust price based on difference
    const finalPrice = basePrice + (vehicleOffset - bookingOffset);

    return finalPrice;
  };

  const days = calculateRideDays(booking?.ride_start, booking?.ride_end);

  const dailyRate = Number(booking?.daily_rate || 0);
  const kmPrice = Number(booking?.km_price || 0);
  const meter = Number(booking?.meter_reading || 0);
  const advance = Number(booking?.preBookingFee || 0);
  const totalPrice = Number(booking?.total_price || 0);

  let finalAmount = 0;

  if (booking?.booking_type == "Reserve") {
    const base = days * dailyRate + kmPrice * meter;
    const gstAmount = ((base + advance) * GST) / 100;

    finalAmount = base + gstAmount - advance; // ✅ correct subtraction
  } else {
    finalAmount = totalPrice + (totalPrice * GST) / 100;
  }

  if (!booking) return <p>Loading...</p>;

  return (
    <>
      <div className="container" style={{ padding: "20px", marginTop: "35px" }}>
        <h5 className="heading-title mt-4">Booking Details</h5>

        <div className="card highlight-card shadow-sm p-3 mb-2">
          <h5 className="heading-title text-danger">* Live Updates</h5>
          <b className="text-success">
            {booking.status == 0
              ? "Waiting for Admin Approval"
              : booking.status == 1
                ? "Waiting for Driver to Confirm"
                : booking.status == 2
                  ? "Driver Accepted the Ride"
                  : booking.status == 3
                    ? "Driver Started the Trip"
                    : booking.status == 4
                      ? "Trip Ended! Confirm End Trip"
                      : booking.status == 6
                        ? "Booking Cancelled"
                        : "Customer Booking Pending"}
          </b>
        </div>

        <div className="card highlight-card shadow-sm p-3 mb-2">
          <p className="mb-0">
            Pick up: <b> {booking.pickup}</b>
          </p>
          <p className="mb-0">
            Drop: <b>{booking.drop}</b>
          </p>
          <p className="mb-0">
            Pick-up Time:{" "}
            <b>
              {formatTimestamp(booking.pickupTime) ||
                formatTimestamp(booking.created_at)}
            </b>
          </p>
        </div>

        <div className="card highlight-card shadow-sm p-3 mb-2">
          {booking.booking_type == "Reserve" ? (
            <>
              <div className="row">
                <div className="col-6">
                  <p className="mb-0">No of Days:</p>
                </div>
                <div className="col-6" style={{ textAlign: "right" }}>
                  <b className="mb-0">{booking.NoOfDays} </b> Days
                </div>

                <div className="col-6">
                  <p className="mb-0">KM Rate:</p>
                </div>
                <div className="col-6" style={{ textAlign: "right" }}>
                  <b className="mb-0">{formatINR(booking.km_price)}</b>
                </div>
                <div className="col-6">
                  <p className="mb-0">Daily Rate:</p>
                </div>
                <div className="col-6" style={{ textAlign: "right" }}>
                  <b className="mb-0">{formatINR(booking.daily_rate)}</b>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="row">
                <div className="col-6">
                  <p className="mb-0">Vehicle Preference:</p>
                </div>
                <div className="col-6" style={{ textAlign: "right" }}>
                  <b style={{ textTransform: "capitalize", color: "red" }}>
                    {booking.vehicle_type}
                  </b>
                </div>

                <div className="col-4">
                  <p className="mb-0">Total Amount:</p>
                </div>
                <div
                  className="col-8"
                  style={{ textAlign: "right", color: "grey" }}
                >
                  <b style={{ color: "green" }}>
                    {booking.confirm_price == 1 ? (
                      <>
                        <h6 className="mb-2" style={{ fontWeight: "900" }}>
                          {booking.total_price}
                        </h6>
                      </>
                    ) : (
                      formatINR(
                        booking.total_price + (booking.total_price * GST) / 100,
                      )
                    )}
                  </b>{" "}
                  <span style={{ fontSize: "11px" }}></span>
                </div>

                <div className="col-6">
                  <p className="mb-0">Breakdown:</p>
                </div>
                <div
                  className="col-6"
                  style={{ textAlign: "right", color: "grey" }}
                >
                  <p className="mb-0">
                    {formatINR(booking.total_price)} +{" "}
                    {formatINR((booking.total_price * GST) / 100)}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="card highlight-card shadow-sm p-3 mb-2">
          {vehicle ? (
            <div className="d-flex gap-2 align-items-center justify-content-between">
              <div>
                <p className="mb-0">
                  Vehicle:{" "}
                  <b>
                    <span style={{ textTransform: "capitalize" }}>
                      {booking.veh_color}
                    </span>{" "}
                    {vehicle.name}
                  </b>
                </p>
                <p className="mb-0">
                  Number: <b>{booking.veh_number}</b>
                </p>
                <p className="mb-0">
                  Fuel: <b>{vehicle.fuel}</b>
                </p>
              </div>
              <img src={vehicle.img_url} alt="vehicle" width={110} />
            </div>
          ) : null}
          <div className="d-flex gap-2">
            <button
              disabled={booking.status >= 3}
              className="btn btn-secondary mt-2 btn-sm"
              onClick={() => setShowVehicleModal(true)}
            >
              {booking.vehicle_id ? "Change Vehicle" : "Choose Vehicle"}
            </button>

            <button
              disabled={booking.status >= 3}
              className="btn btn-dark mt-2 btn-sm"
              onClick={() => setShowVehDetailsModal(true)}
            >
              Update Vehicle Details
            </button>
          </div>
        </div>

        <div className="card highlight-card shadow-sm p-3 mb-2">
          {drivers ? (
            <div className="d-flex gap-2 align-items-center justify-content-between mt-2">
              <div className="d-flex gap-2">
                <img
                  src={drivers.profile_img || driver}
                  width={40}
                  height={40}
                  style={{
                    borderRadius: "8px",
                    objectFit: "cover",
                  }}
                  alt="driver"
                />
                <div>
                  <p className="mb-0">
                    <b>{drivers.name}</b>
                  </p>
                  <p className="mb-0">
                    <FaPhone className="icon text-success" />{" "}
                    <b>{drivers.phone}</b>
                  </p>
                </div>
              </div>

              {/* <Link
                  to={`/driver-booking/${id}`}
                  className="btn btn-dark btn-sm ms-auto"
                  // onClick={() => setShowDriverModal(true)}
                >
                  <BsSendFill  />
                </Link> */}

              <div className="position-relative">
                <button
                  className="btn btn-dark btn-sm ms-auto"
                  onClick={() => setShowDriverActions(!showDriverActions)}
                >
                  <HiDotsVertical />
                </button>

                {showDriverActions && (
                  <div
                    className="card shadow-sm bg-dark text-light position-absolute mt-1"
                    style={{
                      right: 0,
                      minWidth: "140px",
                      zIndex: 1000,
                      fontSize: "13px",
                    }}
                  >
                    <button
                      disabled={booking.status >= 3}
                      className="dropdown-item text-start p-2"
                      onClick={() => setShowDriverModal(true)}
                    >
                      {booking.driver_id ? "Change Driver" : "Choose Driver"}
                    </button>

                    {booking.veh_number ? (
                      <>
                        <Link
                          className="dropdown-item text-start p-2"
                          to={`/driver-booking/${id}`}
                        >
                          Driver Panel
                        </Link>

                        <button
                          className="dropdown-item text-start p-2"
                          onClick={() => {
                            setShowDriverActions(false);
                            handleShare();
                          }}
                        >
                          Share Link
                        </button>
                      </>
                    ) : (
                      <>
                        <Link
                          className="dropdown-item text-start p-2"
                          style={{ cursor: "no-allowed" }}
                          onClick={(e) => {
                            e.preventDefault();
                            toast.error("Update Vehicle Details!");
                          }}
                        >
                          Driver Panel
                        </Link>

                        <button
                          className="dropdown-item text-start p-2"
                          style={{ cursor: "no-allowed" }}
                          onClick={(e) => {
                            e.preventDefault();
                            toast.error("Update Vehicle Details!");
                          }}
                        >
                          Share Link
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="d-flex justify-content-between align-items-center">
              <p className="text-muted mb-0">No driver assigned</p>

              <button
                disabled={booking.status >= 3}
                className="btn btn-dark btn-sm"
                onClick={() => setShowDriverModal(true)}
              >
                Choose Driver
              </button>
            </div>
          )}
        </div>

        <div className="card highlight-card shadow-sm p-3 mb-2">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <p className="mb-0">
                Passenger Name: <b>{customer?.name || booking.customer_name}</b>
              </p>
              <p className="mb-0">
                Passenger Phone:{" "}
                <b>{customer?.phone || booking.customer_phone}</b>
              </p>
              <p className="mb-0">
                Passengers: <b>{booking.passengers}</b>
              </p>
            </div>

            <a
              href={`tel:${booking.customer?.phone || booking.customer_phone}`}
              className="btn btn-dark"
            >
              <FaPhone className="icon" />
            </a>
          </div>
        </div>

        {booking.status >= 4 ? (
          <>
            <div className="card highlight-card shadow-sm p-3 mb-2">
              <p className="mb-1">
                <b>Ride Summary</b>
              </p>
              <div className="row">
                <div className="col-4">
                  <small style={{ fontSize: "12px" }}>Ride Started :</small>
                </div>
                <div style={{ textAlign: "right" }} className="col-8">
                  <b style={{ fontSize: "14px" }}>
                    {formatTimestamp(booking.ride_start) || ""}
                  </b>
                </div>
                <div className="col-4">
                  <small style={{ fontSize: "12px" }}>Ride Ended :</small>
                </div>
                <div style={{ textAlign: "right" }} className="col-8">
                  <b style={{ fontSize: "14px" }}>
                    {formatTimestamp(booking.ride_end) || ""}
                  </b>
                </div>
              </div>
              <hr />
              <div className="row">
                {booking.booking_type == "Reserve" ? (
                  <>
                    <div className="col-8">
                      <small className="mb-0" style={{ fontSize: "12px" }}>
                        Daily Charge{" "}
                        <i className="mb-0">
                          ({" "}
                          {calculateRideDays(
                            booking.ride_start,
                            booking.ride_end,
                          )}{" "}
                          Day(s) x ₹{booking.daily_rate} )
                        </i>{" "}
                        :
                      </small>
                    </div>
                    <div style={{ textAlign: "right" }} className="col-4">
                      <b style={{ fontSize: "14px" }}>
                        ₹
                        {parseFloat(
                          calculateRideDays(
                            booking.ride_start,
                            booking.ride_end,
                          ) * booking.daily_rate,
                        ).toFixed(2)}
                      </b>
                    </div>
                    <div className="col-8">
                      <small style={{ fontSize: "12px" }}>
                        Total KM Rate{" "}
                        <i>
                          ( ₹{booking.km_price} x {booking.meter_reading} )
                        </i>
                        :
                      </small>
                    </div>
                    <div style={{ textAlign: "right" }} className="col-4">
                      <b style={{ fontSize: "14px" }}>
                        ₹
                        {parseFloat(
                          booking.km_price * booking.meter_reading,
                        ).toFixed(2)}
                      </b>
                    </div>
                    <div className="col-8">
                      <small className="mb-0" style={{ fontSize: "12px" }}>
                        GST <i>( 5% )</i> :
                      </small>
                    </div>
                    <div style={{ textAlign: "right" }} className="col-4">
                      <b style={{ fontSize: "14px" }}>
                        {formatINR(
                          ((calculateRideDays(
                            booking.ride_start,
                            booking.ride_end,
                          ) *
                            booking.daily_rate +
                            booking.km_price * booking.meter_reading +
                            booking.preBookingFee) *
                            GST) /
                            100,
                        )}
                      </b>
                    </div>
                       <div className="col-8">
                                          <small style={{ fontSize: "12px" }}>
                                            Advance Amount :
                                          </small>
                                        </div>
                                        <div style={{ textAlign: "right" }} className="col-4">
                                          <i style={{ fontSize: "14px" }}>
                                            - {formatINR(booking.preBookingFee)}
                                          </i>
                                        </div>
                  </>
                ) : null}

                <div className="col-6">
                  <small style={{ fontSize: "15px" }}>Grand Total :</small>
                </div>
                <div style={{ textAlign: "right" }} className="col-6">
                  <b style={{ fontSize: "22px", color: "green" }}>
                    {formatINR(finalAmount)}
                  </b>
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>

      {/* ========================================================= */}
      {/* VEHICLE MODAL */}
      {/* ========================================================= */}

      {showVehicleModal && (
        <div
          className="modal d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content p-3">
              <div className="d-flex justify-content-between align-items-center">
                <h5>Select Vehicle</h5>
              </div>
              {booking.booking_type == "Reserve" ? null : (
                <>
                  <p className="mb-0" style={{ fontSize: "13px" }}>
                    <span style={{ color: "red" }}>**</span>Preference:{" "}
                    <b style={{ textTransform: "capitalize", color: "green" }}>
                      {booking?.vehicle_type}
                    </b>
                  </p>
                </>
              )}

              <div className="admin-select-car row mt-2">
                {allVehicles &&
                  allVehicles.map((v) => {
                    const isSelected = selectedVehicleId == v.id;

                    return (
                      <div key={v.id} className="col-md-4 mb-2">
                        <div
                          className="card shadow-sm p-2 h-100 "
                          style={{
                            cursor: "pointer",
                            // display: "flex",
                            // justifyContent: "space-between",
                            border: isSelected
                              ? "2px solid #198754"
                              : "1px solid #e5e5e5",
                            backgroundColor: isSelected ? "#e9f7ef" : "#fff",
                          }}
                          onClick={() => setSelectedVehicleId(v.id)}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h6 className="mb-1">
                                {v.name}{" "}
                                {booking.booking_type == "Reserve" && (
                                  <small style={{ color: "red" }}>
                                    (
                                    {booking.booking_type == "Reserve"
                                      ? null
                                      : "₹" + getVehiclePrice(v, booking)}
                                    )
                                  </small>
                                )}
                              </h6>
                              <p className="mb-0">Fuel: {v.fuel}</p>
                              {booking.booking_type == "Reserve" ? (
                                <>
                                  <small style={{ color: "green" }}>
                                    ₹{v.daily_price}/day | ₹{v.km_price}/km
                                  </small>
                                </>
                              ) : null}
                            </div>
                            <p
                              className="meta-badge mb-0 mt-0"
                              style={{
                                backgroundColor:
                                  v.type == "economy"
                                    ? "rgb(253, 234, 234)"
                                    : v.type == "standard"
                                      ? "rgb(230, 230, 230)"
                                      : "#ceecde",

                                textTransform: "capitalize",
                                color:
                                  v.type == "economy"
                                    ? "#b62626"
                                    : v.type == "standard"
                                      ? "black"
                                      : "#096d3f",
                              }}
                            >
                              {v.type}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
              <hr />

              {booking.booking_type == "Reserve" ? null : (
                <>
                  <h6 className="mt-0">
                    {" "}
                    <span style={{ color: "red" }}>**</span> Confirm Ride Rate
                  </h6>
                  <div className="d-flex gap-2 align-items-center">
                    <input
                      className="form-control"
                      readOnly
                      value="₹"
                      style={{ width: "50px", textAlign: "center" }}
                    />

                    <input
                      className="form-control"
                      required
                      type="number"
                      value={booking?.total_price || ""}
                      placeholder="Ride Rate"
                      onChange={(e) =>
                        setBooking({
                          ...booking,
                          total_price: Number(e.target.value),
                        })
                      }
                    />
                    <input
                      className="form-control"
                      readOnly
                      value=".00"
                      style={{ width: "60px", textAlign: "center" }}
                    />
                  </div>
                </>
              )}

              {/* Footer Buttons */}
              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowVehicleModal(false);
                    setSelectedVehicleId(null);
                  }}
                >
                  Cancel
                </button>

                <button
                  className="btn btn-dark"
                  disabled={!selectedVehicleId}
                  onClick={confirmVehicleSelection}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add vehicle details Modal*/}

      {showVehDetailsModal && (
        <div
          className="modal d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content p-4">
              <div className="d-flex justify-content-between align-items-center">
                <h5>Update Vehicle Details</h5>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => setShowVehDetailsModal(false)}
                >
                  ✕
                </button>
              </div>

              <div className="mt-3">
                <input
                  type="text"
                  className="form-control mb-2"
                  style={{ textTransform: "uppercase" }}
                  placeholder="Vehicle Number"
                  value={vehDetails.veh_number}
                  onChange={(e) =>
                    setVehDetails({
                      ...vehDetails,
                      veh_number: e.target.value.toUpperCase(),
                    })
                  }
                />

                <select
                  className="form-control mb-2"
                  value={vehDetails.veh_color}
                  onChange={(e) =>
                    setVehDetails({
                      ...vehDetails,
                      veh_color: e.target.value,
                    })
                  }
                >
                  <option value="">Select Vehicle Color</option>
                  <option value="white">White</option>
                  <option value="black">Black</option>
                  <option value="grey">Grey</option>
                  <option value="silver">Silver</option>
                  <option value="blue">Blue</option>
                  <option value="green">Green</option>
                </select>

                {/* <input
                  type="text"
                  className="form-control"
                  placeholder="Vehicle Color"
                  value={vehDetails.veh_color}
                  onChange={(e) =>
                    setVehDetails({
                      ...vehDetails,
                      veh_color: e.target.value,
                    })
                  }
                /> */}
              </div>

              <button
                className="btn btn-dark mt-3 w-100"
                onClick={updateVehicleDetails}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {showDriverModal && (
        <div
          className="modal d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content p-4">
              <div className="d-flex justify-content-between align-items-center">
                <h5>Select Driver</h5>

                <button
                  className="btn btn-sm btn-success"
                  onClick={() => setShowAddDriverModal(true)}
                >
                  + Add New Driver
                </button>
              </div>

              <div className="row mt-3">
                {allDrivers.map((d) => {
                  const alreadySelected = booking?.driver_id === d.id;

                  const disabled = isDriverBooked(d.id) && !alreadySelected;

                  const isSelected = selectedDriverId === d.id;

                  return (
                    <div className="col-md-4 mb-2" key={d.id}>
                      <div
                        className="card shadow-sm p-2 h-100"
                        style={{
                          cursor: disabled ? "not-allowed" : "pointer",
                          opacity: disabled ? 0.5 : 1,
                          border: isSelected
                            ? "2px solid #198754"
                            : "1px solid #e5e5e5",
                          backgroundColor: isSelected ? "#e9f7ef" : "#fff",
                        }}
                        onClick={() => {
                          if (!disabled) {
                            setSelectedDriverId(d.id);
                          }
                        }}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <img
                            src={d.profile_img || driver}
                            alt="driver"
                            style={{
                              width: 45,
                              height: 45,
                              borderRadius: "8px",
                              objectFit: "cover",
                              marginBottom: "0",
                            }}
                          />

                          <div>
                            <h6 className="mb-1">{d.name}</h6>
                            <p className="mb-0 text-muted">{d.phone}</p>

                            {alreadySelected && (
                              <small
                                className="text-success mt-1"
                                style={{ fontSize: "12px" }}
                              >
                                ✔ Currently Assigned
                              </small>
                            )}

                            {disabled && (
                              <small className="text-danger mt-1">
                                ✖ Booked For Selected Dates
                              </small>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="d-flex justify-content-end gap-2 mt-3">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setShowDriverModal(false);
                    setSelectedDriverId(null);
                  }}
                >
                  Cancel
                </button>

                <button
                  className="btn btn-dark btn-sm"
                  disabled={!selectedDriverId}
                  onClick={confirmDriverSelection}
                >
                  Confirm Selection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddDriverModal && (
        <div
          className="modal d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>Add New Driver</h5>

                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => setShowAddDriverModal(false)}
                >
                  ✕
                </button>
              </div>

              <input
                required
                className="form-control mb-2"
                placeholder="Name"
                value={newDriver.name}
                onChange={(e) =>
                  setNewDriver({
                    ...newDriver,
                    name: e.target.value,
                  })
                }
              />

              <input
                required
                className="form-control mb-2"
                placeholder="Phone"
                type="tel"
                maxLength={10}
                value={newDriver.phone}
                onChange={(e) =>
                  setNewDriver({
                    ...newDriver,
                    phone: e.target.value,
                  })
                }
              />

              <input
                type="file"
                accept="image/*"
                className="form-control mb-2"
                onChange={handleImageSelect}
              />

              <button
                className="btn btn-dark"
                disabled={isSavingDriver}
                onClick={async () => {
                  if (!newDriver.name || !newDriver.phone) {
                    toast.error("Name and Phone required");
                    return;
                  }

                  try {
                    setIsSavingDriver(true); // 🔥 START LOADER

                    const { data, error } = await supabase
                      .from("Drivers")
                      .insert([
                        {
                          name: newDriver.name,
                          phone: newDriver.phone,
                        },
                      ])
                      .select()
                      .single();

                    if (error) {
                      console.error(error);
                      setIsSavingDriver(false);
                      return;
                    }

                    const driverId = data.id;
                    let imageUrl = "";

                    if (selectedImage) {
                      const fileName = `${driverId}.webp`;

                      const { error: uploadError } = await supabase.storage
                        .from("drivers")
                        .upload(fileName, selectedImage, {
                          contentType: "image/webp",
                          upsert: true,
                        });

                      if (!uploadError) {
                        const { data: publicUrlData } = supabase.storage
                          .from("drivers")
                          .getPublicUrl(fileName);

                        imageUrl = publicUrlData.publicUrl;
                      }
                    }

                    if (imageUrl) {
                      await supabase
                        .from("Drivers")
                        .update({ profile_img: imageUrl })
                        .eq("id", driverId);
                    }

                    fetchDrivers();
                    setShowAddDriverModal(false);
                    setNewDriver({ name: "", phone: "" });
                    setSelectedImage(null);

                    toast.success("Driver added successfully 🚀");
                  } catch (err) {
                    console.error(err);
                  } finally {
                    setIsSavingDriver(false); // 🔥 STOP LOADER
                  }
                }}
              >
                {isSavingDriver ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    ></span>
                    Saving...
                  </>
                ) : (
                  "Save Driver"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCropModal && (
        <div
          className="modal d-block"
          style={{ background: "rgba(0,0,0,0.6)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content p-3">
              <h5>Crop Image (1:1 Square)</h5>

              <div style={{ position: "relative", width: "100%", height: 400 }}>
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1} // 🔥 Force 1:1 ratio
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>

              <div className="d-flex justify-content-between mt-3">
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(e.target.value)}
                />

                <div>
                  <button
                    className="btn btn-secondary me-2"
                    onClick={() => setShowCropModal(false)}
                  >
                    Cancel
                  </button>

                  <button
                    className="btn btn-dark"
                    onClick={async () => {
                      const croppedWebp = await getCroppedWebpImage();
                      setSelectedImage(croppedWebp);
                      setShowCropModal(false);
                    }}
                  >
                    Crop & Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default BookingDetails;
